<template>
  <TemplateBase>
    <main class="col-span-full row-start-2">
      <OrganismNavigationBreadcrumb :path="breadcrumbs" class="container mb-4 mx-auto pt-10 px-4 md:mb-8" />
      <div class="mb-8 mx-auto w-full sm:w-8/12">
        <div class="mb-6 mx-2 pb-12 pt-16 px-4 bg-white rounded-xl sm:mx-0 sm:px-12 lg:px-16">
          <h1 class="break-words mb-3 text-2xl font-black leading-none uppercase md:text-3xl md:font-bold lg:text-5xl">{{ page.title }}</h1>
          <div class="flex flex-col py-6 lg:flex-row lg:flex-[1_1_33%]">
            <div class="mb-2 lg:mr-2 lg:mb-0 lg:basis-1/3">
              <FormKit v-model="areaPreference" type="select" select-icon="chevronDown" :options="[{ value: '*', label: t_('all-area-preferences') }].concat(areaPreferences)" />
            </div>
            <div class="mb-2 lg:mr-2 lg:mb-0 lg:basis-1/3">
              <FormKit v-model="department" type="select" select-icon="chevronDown" :options="[{ value: '*', label: t_('all-departments') }].concat(departments)" />
            </div>
            <div class="lg:mr-2 lg:basis-1/3">
              <FormKit
                v-if="page.jobOfferType === 'volunteer'"
                v-model="jobFunctionVolunteer"
                type="select"
                select-icon="chevronDown"
                :options="[{ value: '*', label: t_('all-job-functions') }].concat(jobFunctionsVolunteer)"
              />
              <FormKit
                v-else
                v-model="jobFunctionPaid"
                type="select"
                select-icon="chevronDown"
                :options="[{ value: '*', label: t_('all-job-functions') }].concat(jobFunctionsPaid)"
              />
            </div>
          </div>

          <!-- ACTIVE FACETS -->
          <div v-if="hits.length === 0 && (areaPreference !== '*' || department !== '*' || jobFunction !== '*')" class="md:order-0 order-1 mt-2 md:mt-0 lg:flex">
            <ul class="flex flex-auto flex-wrap md:mr-4">
              <li v-if="areaPreference !== '*'" class="mb-2 mr-2 md:mb-4 md:mr-4" @click="reset">
                <MoleculeFilter :selected="true" :text="areaPreference" />
              </li>
              <li v-if="department !== '*'" class="mb-2 mr-2 md:mb-4 md:mr-4" @click="reset">
                <MoleculeFilter :selected="true" :text="department" />
              </li>
              <li v-if="jobFunction !== '*'" class="mb-2 mr-2 md:mb-4 md:mr-4" @click="reset">
                <MoleculeFilter :selected="true" :text="jobFunction" />
              </li>
            </ul>
          </div>

          <div>
            <ul class="mb-12 border-b-2 border-solid border-app-green-100">
              <li
                v-for="(uri, index) in hits"
                :key="uri"
                :class="{ 'border-t-2  border-app-green-100': index < hits.length }"
                class="px-2 py-4 hover:bg-app-green-100 border-solid cursor-pointer transform transition duration-200 ease-in-out sm:px-8"
              >
                <OrganismLoaderFromUri v-slot="hit" :uri="uri">
                  <AtomLink :url="hit.url" class="flex flex-column">
                    <div class="w-3/4">
                      <span class="font-bold block"> {{ hit.title }} </span>
                      <span class="hidden sm:inline-block"> {{ hit.departmentTitle }}</span>
                      <span v-if="hit.departmentTitle && hit.location" class="hidden sm:inline-block">•</span>
                      <span class="hidden sm:inline-block"> {{ hit.location }}</span>
                      <span v-if="hit.jobOfferWorkloadDescription && hit.location" class="hidden sm:inline-block">•</span>
                      <span class="hidden sm:inline-block"> {{ hit.jobOfferWorkloadDescription }} </span>
                    </div>
                    <div class="self-end ml-auto">
                      <AtomIconArrowRight class="ml-auto w-5 text-app-green" />
                    </div>
                  </AtomLink>
                </OrganismLoaderFromUri>
              </li>
            </ul>
            <p v-show="hits.length === 0" class="col-span-full mt-5 text-sm font-semibold leading-snug lg:col-span-9 text-black">
              {{ t_('no-results') }}
            </p>
            <section class="container relative mx-auto">
              <OrganismPagination :count="totalHits" :limit="hitsPerPage" :page="currentPage" @click="({ page }) => setCurrentPage(page)" />
            </section>
          </div>
        </div>

        <CallToActionList v-if="ctas" :ctas="ctas" class="mb-12 mx-2 sm:mx-0" />
      </div>
    </main>
  </TemplateBase>
</template>

<script setup lang="ts">
import { onMounted, ref, toRefs, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useJobOfferOverviewStore } from '~/store/job-offer-overview'
import { useGlobalStore } from '~/store/global'
import { routerQueryIsEqual } from '~/composables/setup-dynamic-url'
import { setupTranslationPrefixer } from '~/composables/util'
import { useCtaStore } from '~/store/call-to-action'
import CallToActionList from '~/components/Organism/CallToActionList.vue'

interface Props {
  breadcrumbs: any[]
  page: object
}

const props = defineProps<Props>()
const { breadcrumbs, page } = toRefs(props) // Unknown if more properties are needed from page

const route = useRoute()
const jobOfferOverviewStore = useJobOfferOverviewStore()
const globalStore = useGlobalStore()
const cta = ref<any | null>(null)

onMounted(async () => {
  cta.value = await globalStore.fetchJobOfferCtas()
})

const ctaType =
  props.page.jobOfferType === 'volunteer' ? 'jobOfferVolunteer' : props.page.jobOfferType === 'paid' || props.page.jobOfferType === 'internship' ? 'jobOfferPaid' : null

const ctas = await useCtaStore().fetch(ctaType)

const {
  areaPreference: areaPreferenceRef,
  areaPreferences,
  department: departmentRef,
  departments,
  jobFunctionVolunteer: jobFunctionVolunteerRef,
  jobFunctionsVolunteer,
  jobFunctionPaid: jobFunctionPaidRef,
  jobFunctionsPaid,
  currentPage,
  getHits: hits,
  hitsPerPage,
  totalHits,
  urlParameters,
} = storeToRefs(jobOfferOverviewStore)

const reset = jobOfferOverviewStore.reset
const setCurrentPage = jobOfferOverviewStore.setCurrentPage

const areaPreference = computed({
  get: () => areaPreferenceRef.value,
  set: (value) => {
    jobOfferOverviewStore.setAreaPreference(value)
    jobOfferOverviewStore.setCurrentPage(1)
  },
})
const department = computed({
  get: () => departmentRef.value,
  set: (value) => {
    jobOfferOverviewStore.setDepartment(value)
    jobOfferOverviewStore.setCurrentPage(1)
  },
})
const jobFunctionVolunteer = computed({
  get: () => jobFunctionVolunteerRef.value,
  set: (value) => {
    jobOfferOverviewStore.setJobFunctionVolunteer(value)
    jobOfferOverviewStore.setCurrentPage(1)
  },
})
const jobFunctionPaid = computed({
  get: () => jobFunctionPaidRef.value,
  set: (value) => {
    jobOfferOverviewStore.setJobFunctionPaid(value)
    jobOfferOverviewStore.setCurrentPage(1)
  },
})

const { t_ } = setupTranslationPrefixer('JobOfferOverview')

watch(urlParameters, (query) => {
  if (!routerQueryIsEqual(route.query, query)) {
    navigateTo({ query })
  }
})
</script>
